import React, { useEffect, useState, useCallback } from "react";
import {
  Crown, Zap, ArrowRight, ArrowDown, Check, X, AlertTriangle,
  Users, Layers, BookOpen, UserCog, Shield,
  Calendar, TrendingUp, Clock, Sparkles,
  ChevronRight, Star, Loader, HardDrive, Percent, BadgeIndianRupee,
  Receipt, Download, History,
} from "lucide-react";
import { useNavigate } from "react-router-dom"; // eslint-disable-line no-unused-vars
import apiClient from "../../utils/apiClient";
import "./billing.scss";

// ── Labels ──
const LIMIT_LABELS = {
  maxStudentsPerBatch: { label: "Students/Batch", icon: Users },
  maxBatches: { label: "Batches", icon: Layers },
  maxCourses: { label: "Courses", icon: BookOpen },
  maxInstructors: { label: "Instructors", icon: UserCog },
  maxAdmins: { label: "Admins", icon: Shield },
  maxBranches: { label: "Branches", icon: Layers },
};

const USAGE_KEYS = {
  batches: "maxBatches",
  courses: "maxCourses",
  instructors: "maxInstructors",
  admins: "maxAdmins",
  branches: "maxBranches",
};

const TOGGLE_LABELS = {
  zoomV2: "Zoom V2 Integration",
  recordings: "Class Recordings",
  whatsapp: "WhatsApp Notifications",
  customDomain: "Custom Domain",
  aiMock: "AI Mock Interviews",
  aiLearning: "AI Learning Assistant",
  aiQuestionBank: "AI Question Bank",
  feeManagement: "Fee Management",
  enquiryManagement: "Enquiry Management",
  interviews: "Interview Scheduling",
  codingPractice: "Coding Practice",
  assignments: "Assignments",
  customBranding: "Custom Branding",
  apiAccess: "API Access",
  enquiryFormBuilder: "Enquiry Form Builder",
  multiBranch: "Multi-Branch Management",
};

const formatPrice = (amount) => {
  if (!amount || amount === 0) return "Free";
  return `₹${Number(amount).toLocaleString("en-IN")}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "numeric", month: "short", year: "numeric",
  });
};

// ── Usage Meter (single resource) ──
function UsageMeter({ label, icon: Icon, used, max }) {
  const isUnlimited = !max || max === 0;
  const percent = isUnlimited ? 0 : Math.min(Math.round((used / max) * 100), 100);
  const isWarning = percent >= 80 && percent < 95;
  const isDanger = percent >= 95;
  const colorClass = isDanger ? "danger" : isWarning ? "warning" : "normal";

  return (
    <div className="bp-usage-meter">
      <div className="bp-usage-meter__header">
        <div className="bp-usage-meter__label">
          <Icon size={16} />
          <span>{label}</span>
        </div>
        <span className="bp-usage-meter__count">
          <strong>{used}</strong>
          {isUnlimited ? " (Unlimited)" : ` / ${max}`}
        </span>
      </div>
      {!isUnlimited && (
        <div className="bp-usage-meter__track">
          <div
            className={`bp-usage-meter__fill bp-usage-meter__fill--${colorClass}`}
            style={{ width: `${percent}%` }}
          />
        </div>
      )}
      {isDanger && (
        <div className="bp-usage-meter__alert">
          <AlertTriangle size={12} /> Almost at limit — upgrade to add more
        </div>
      )}
    </div>
  );
}

// ── Feature Badge (current plan feature list) ──
function FeatureItem({ label, enabled }) {
  return (
    <div className={`bp-feature-item ${!enabled ? "bp-feature-item--disabled" : ""}`}>
      {enabled ? (
        <Check size={14} className="bp-feature-item__icon bp-feature-item__icon--yes" />
      ) : (
        <X size={14} className="bp-feature-item__icon bp-feature-item__icon--no" />
      )}
      <span>{label}</span>
    </div>
  );
}

// ── Plan Card (supports current / upgrade / downgrade) ──
function PlanCard({ plan, currentPlan, billingCycle, taxConfig, onSelect, paymentLoading }) {
  const tag = plan._planTag || "upgrade"; // 'current' | 'upgrade' | 'downgrade' | 'current-tier'
  const isCurrent = tag === "current" || tag === "current-tier";
  const isDowngrade = tag === "downgrade";
  const isNext = plan.isPopular && !isCurrent;
  const cycle = billingCycle || "monthly";
  const priceKey =
    cycle === "yearly" ? "priceYearly" : cycle === "quarterly" ? "priceQuarterly" : "priceMonthly";
  const periodLabel =
    cycle === "yearly" ? "/year" : cycle === "quarterly" ? "/quarter" : "/month";

  const basePrice = plan[priceKey] || 0;
  const hasTax = taxConfig?.enabled && taxConfig.percentage > 0;

  // Compute display amounts
  let displaySubtotal = basePrice;
  let displayTax = 0;
  let displayTotal = basePrice;

  if (hasTax) {
    if (taxConfig.includeInPrice) {
      displayTotal = basePrice;
      displaySubtotal = Math.round(basePrice / (1 + taxConfig.percentage / 100));
      displayTax = displayTotal - displaySubtotal;
    } else {
      displaySubtotal = basePrice;
      displayTax = Math.round(basePrice * taxConfig.percentage / 100);
      displayTotal = displaySubtotal + displayTax;
    }
  }

  // Count how many MORE features this plan unlocks vs current
  const newToggles = Object.keys(TOGGLE_LABELS).filter(
    (k) => plan[k] && !currentPlan?.[k]
  );
  // Features that would be LOST on downgrade
  const lostToggles = Object.keys(TOGGLE_LABELS).filter(
    (k) => currentPlan?.[k] && !plan[k]
  );

  const cardClass = [
    "bp-upgrade-card",
    isNext ? "bp-upgrade-card--recommended" : "",
    isCurrent ? "bp-upgrade-card--current" : "",
    isDowngrade ? "bp-upgrade-card--downgrade" : "",
  ].filter(Boolean).join(" ");

  return (
    <div className={cardClass}>
      {isCurrent && (
        <div className="bp-upgrade-card__ribbon bp-upgrade-card__ribbon--current">
          <Crown size={12} /> Current Plan
        </div>
      )}
      {isNext && !isCurrent && (
        <div className="bp-upgrade-card__ribbon">
          <Star size={12} /> Recommended
        </div>
      )}
      {isDowngrade && !isCurrent && (
        <div className="bp-upgrade-card__ribbon bp-upgrade-card__ribbon--downgrade">
          <ArrowDown size={12} /> Downgrade
        </div>
      )}
      <div className="bp-upgrade-card__header">
        <h3 className="bp-upgrade-card__name">{plan.name}</h3>
        {plan.isFree ? (
          <div className="bp-upgrade-card__price">
            <span className="bp-upgrade-card__amount">Free</span>
          </div>
        ) : (
          <div className="bp-upgrade-card__price">
            <span className="bp-upgrade-card__amount">{formatPrice(displayTotal)}</span>
            <span className="bp-upgrade-card__period">{periodLabel}</span>
          </div>
        )}
        {hasTax && !plan.isFree && (
          <div className="bp-upgrade-card__tax-info">
            {formatPrice(displaySubtotal)} + {taxConfig.label} {taxConfig.percentage}% ({formatPrice(displayTax)})
          </div>
        )}
      </div>

      <div className="bp-upgrade-card__limits">
        {Object.entries(LIMIT_LABELS).map(([key, { label }]) => (
          <div key={key} className="bp-upgrade-card__limit-row">
            <span>{label}</span>
            <strong>{plan[key] || "Unlimited"}{key === "storageGB" && plan[key] ? " GB" : ""}</strong>
          </div>
        ))}
      </div>

      <div className="bp-upgrade-card__extra-info">
        <div className="bp-upgrade-card__extra-row">
          <span><HardDrive size={14} /> Storage</span>
          <strong>{plan.storageGB ? `${plan.storageGB} GB` : "Unlimited"}</strong>
        </div>
        <div className="bp-upgrade-card__extra-row">
          <span><Percent size={14} /> Revenue Share</span>
          <strong>{plan.revenueSharePercent ? `${plan.revenueSharePercent}%` : "0%"}</strong>
        </div>
        {plan.extraStoragePricePer100GB > 0 && (
          <div className="bp-upgrade-card__extra-row">
            <span><BadgeIndianRupee size={14} /> Extra Storage</span>
            <strong>{formatPrice(plan.extraStoragePricePer100GB)} / 100 GB</strong>
          </div>
        )}
      </div>

      {newToggles.length > 0 && !isCurrent && (
        <div className="bp-upgrade-card__new-features">
          <div className="bp-upgrade-card__new-label">
            <Sparkles size={12} /> {isDowngrade ? "Includes" : "Unlocks"} {newToggles.length} new feature{newToggles.length > 1 ? "s" : ""}
          </div>
          {newToggles.slice(0, 4).map((k) => (
            <div key={k} className="bp-upgrade-card__new-item">
              <Check size={12} /> {TOGGLE_LABELS[k]}
            </div>
          ))}
          {newToggles.length > 4 && (
            <div className="bp-upgrade-card__new-item bp-upgrade-card__new-item--more">
              +{newToggles.length - 4} more
            </div>
          )}
        </div>
      )}

      {isDowngrade && lostToggles.length > 0 && (
        <div className="bp-upgrade-card__lost-features">
          <div className="bp-upgrade-card__lost-label">
            <AlertTriangle size={12} /> You will lose {lostToggles.length} feature{lostToggles.length > 1 ? "s" : ""}
          </div>
          {lostToggles.slice(0, 3).map((k) => (
            <div key={k} className="bp-upgrade-card__lost-item">
              <X size={12} /> {TOGGLE_LABELS[k]}
            </div>
          ))}
          {lostToggles.length > 3 && (
            <div className="bp-upgrade-card__lost-item bp-upgrade-card__lost-item--more">
              +{lostToggles.length - 3} more
            </div>
          )}
        </div>
      )}

      {isCurrent ? (
        <div className="bp-upgrade-card__btn bp-upgrade-card__btn--current">
          <Check size={14} /> Your Current Plan
        </div>
      ) : (
        <button
          className={`bp-upgrade-card__btn ${isDowngrade ? "bp-upgrade-card__btn--downgrade" : ""}`}
          onClick={() => onSelect?.(plan)}
          disabled={paymentLoading || plan.isFree}
        >
          {isDowngrade ? (
            <><ArrowDown size={14} /> Downgrade to {plan.name}</>
          ) : (
            <><ArrowRight size={14} /> Upgrade to {plan.name}</>
          )}
        </button>
      )}
    </div>
  );
}

// ═══════════════════════════════════════
// Main Page
// ═══════════════════════════════════════
const MyPlanPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentMessage, setPaymentMessage] = useState(null);
  const [payments, setPayments] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const fetchPlan = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await apiClient.get("/billing/my-plan");
      setData(res);
      if (res.subscription?.billingCycle) {
        setBillingCycle(res.subscription.billingCycle);
      }
    } catch (err) {
      console.error("Failed to load billing info:", err);
      setData(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchPlan();
  }, [fetchPlan]);

  // ── Fetch payment history ──
  const fetchPaymentHistory = useCallback(async () => {
    try {
      const { data: res } = await apiClient.get("/billing/payment-history");
      setPayments(res.payments || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchPaymentHistory();
  }, [fetchPaymentHistory]);

  const handleDownloadReceipt = async (paymentId) => {
    try {
      const response = await apiClient.get(`/billing/payment-receipt/${paymentId}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch {
      setPaymentMessage({ type: "error", text: "Failed to download receipt." });
    }
  };

  // ── Load Razorpay Checkout script dynamically ──
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleUpgradeSelect = async (plan) => {
    if (paymentLoading) return;

    // Free plan — no payment needed
    const priceKey = billingCycle === "yearly" ? "priceYearly" : billingCycle === "quarterly" ? "priceQuarterly" : "priceMonthly";
    if (!plan[priceKey] || plan[priceKey] === 0) {
      setPaymentMessage({ type: "error", text: "This plan is free. Contact support if you want to switch." });
      return;
    }

    setPaymentLoading(true);
    setPaymentMessage(null);

    try {
      // 1. Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        setPaymentMessage({ type: "error", text: "Failed to load payment gateway. Check your internet connection." });
        setPaymentLoading(false);
        return;
      }

      // 2. Create order on backend
      const { data: orderData } = await apiClient.post("/billing/create-order", {
        planId: plan._id,
        billingCycle,
      });

      // 3. Open Razorpay checkout
      const user = JSON.parse(localStorage.getItem("user") || "{}");

      // Build description with tax info if present
      const taxInfo = orderData.tax;
      let description = `${orderData.plan?.name} — ${orderData.plan?.billingCycle} plan`;
      if (taxInfo?.taxAmount > 0) {
        description += ` (incl. ${taxInfo.taxLabel} ${taxInfo.taxPercentage}%)`;
      }

      const options = {
        key: orderData.keyId,
        amount: orderData.amount,
        currency: orderData.currency,
        name: orderData.institute?.name || "Institute",
        description,
        order_id: orderData.orderId,
        prefill: {
          name: user.name || "",
          email: user.email || "",
          contact: user.phone || "",
        },
        theme: { color: "#6366f1" },
        handler: async (response) => {
          // 4. Verify payment on backend
          try {
            const { data: verifyRes } = await apiClient.post("/billing/verify-payment", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            setPaymentMessage({ type: "success", text: verifyRes.message || "Plan upgraded successfully!" });
            // Refresh plan data
            fetchPlan();
          } catch (verifyErr) {
            console.error("Payment verification failed:", verifyErr);
            setPaymentMessage({
              type: "error",
              text: verifyErr?.response?.data?.message || "Payment verification failed. Contact support if amount was deducted.",
            });
          }
          setPaymentLoading(false);
        },
        modal: {
          ondismiss: () => {
            setPaymentLoading(false);
            setPaymentMessage({ type: "error", text: "Payment cancelled." });
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (response) => {
        setPaymentLoading(false);
        setPaymentMessage({
          type: "error",
          text: response.error?.description || "Payment failed. Please try again.",
        });
      });
      rzp.open();
    } catch (err) {
      console.error("Payment initiation failed:", err);
      setPaymentMessage({
        type: "error",
        text: err?.response?.data?.message || "Could not initiate payment. Please try again.",
      });
      setPaymentLoading(false);
    }
  };

  // ── Loading state ──
  if (loading) {
    return (
      <div className="bp-page">
        <div className="bp-loading">
          <div className="bp-loading-spinner" />
          <span>Loading your plan...</span>
        </div>
      </div>
    );
  }

  // ── Error state ──
  if (!data) {
    return (
      <div className="bp-page">
        <div className="bp-empty">
          <AlertTriangle size={40} />
          <h3>Unable to load plan information</h3>
          <p>Please try again later or contact support.</p>
        </div>
      </div>
    );
  }

  const { currentPlan, usage, subscription, upgradePlans = [], taxConfig } = data;
  const isTrialActive = subscription?.status === "trial";
  const isExpired = subscription?.daysRemaining === 0 && subscription?.status !== "active";
  const priceKey =
    billingCycle === "yearly" ? "priceYearly" : billingCycle === "quarterly" ? "priceQuarterly" : "priceMonthly";
  const currentPlanHasPrice = currentPlan && currentPlan[priceKey] > 0;
  const periodLabel =
    billingCycle === "yearly" ? "/year" : billingCycle === "quarterly" ? "/quarter" : "/month";

  return (
    <div className="bp-page">
      {/* ── Page Header ── */}
      <div className="bp-header">
        <div className="bp-header__left">
          <div className="bp-header__icon">
            <Crown size={22} />
          </div>
          <div>
            <h1 className="bp-header__title">Plan & Billing</h1>
            <p className="bp-header__subtitle">
              Manage your subscription, track resource usage, and explore upgrades
            </p>
          </div>
        </div>
      </div>

      {/* ── Trial / Expiry Alert ── */}
      {isTrialActive && (
        <div className="bp-alert bp-alert--trial">
          <Clock size={18} />
          <div className="bp-alert__body">
            <strong>Free Trial — {subscription.daysRemaining} day{subscription.daysRemaining !== 1 ? "s" : ""} remaining</strong>
            <span>{currentPlanHasPrice ? "Subscribe now to activate your plan, or upgrade for more features." : "Upgrade now to keep your data and unlock all features."}</span>
          </div>
          <div className="bp-alert__actions">
            {currentPlanHasPrice && (
              <button
                className="bp-alert__cta bp-alert__cta--primary"
                onClick={() => handleUpgradeSelect(currentPlan)}
                disabled={paymentLoading}
              >
                Subscribe to {currentPlan.name} <ArrowRight size={14} />
              </button>
            )}
            <a href="#upgrade" className="bp-alert__cta">Upgrade Plan <ArrowRight size={14} /></a>
          </div>
        </div>
      )}
      {isExpired && (
        <div className="bp-alert bp-alert--danger">
          <AlertTriangle size={18} />
          <div className="bp-alert__body">
            <strong>Your subscription has expired</strong>
            <span>{currentPlanHasPrice ? "Renew your current plan or upgrade to a new one." : "Renew immediately to prevent data access interruptions."}</span>
          </div>
          <div className="bp-alert__actions">
            {currentPlanHasPrice && (
              <button
                className="bp-alert__cta bp-alert__cta--primary"
                onClick={() => handleUpgradeSelect(currentPlan)}
                disabled={paymentLoading}
              >
                Renew {currentPlan.name} <ArrowRight size={14} />
              </button>
            )}
            <a href="#upgrade" className="bp-alert__cta">Upgrade Plan <ArrowRight size={14} /></a>
          </div>
        </div>
      )}

      {/* ── Payment Status Message ── */}
      {paymentMessage && (
        <div className={`bp-alert bp-alert--${paymentMessage.type === "success" ? "success" : "danger"}`}>
          {paymentMessage.type === "success" ? <Check size={18} /> : <AlertTriangle size={18} />}
          <div className="bp-alert__body">
            <strong>{paymentMessage.text}</strong>
          </div>
          <button className="bp-alert__close" onClick={() => setPaymentMessage(null)}><X size={14} /></button>
        </div>
      )}

      {/* ── Payment Loading Overlay ── */}
      {paymentLoading && (
        <div className="bp-alert bp-alert--info">
          <Loader size={18} className="bp-spin" />
          <div className="bp-alert__body">
            <strong>Processing payment... Please do not close this page.</strong>
          </div>
        </div>
      )}

      {/* ── Current Plan Hero ── */}
      <div className="bp-plan-hero">
        <div className="bp-plan-hero__top">
          <div className="bp-plan-hero__info">
            <div className="bp-plan-hero__name-row">
              <span className="bp-plan-hero__plan-name">{currentPlan?.name || "No Plan"}</span>
              <span className={`bp-plan-hero__status bp-plan-hero__status--${subscription?.status || "inactive"}`}>
                {subscription?.status === "active" ? "Active" : subscription?.status === "trial" ? "Trial" : subscription?.status === "suspended" ? "Suspended" : "Inactive"}
              </span>
            </div>
            <div className="bp-plan-hero__price">
              <span className="bp-plan-hero__amount">
                {currentPlan ? formatPrice(currentPlan[priceKey]) : "—"}
              </span>
              {currentPlan && <span className="bp-plan-hero__period">{periodLabel}</span>}
            </div>
          </div>
          <div className="bp-plan-hero__dates">
            {subscription?.startDate && (
              <div className="bp-plan-hero__date-item">
                <Calendar size={14} />
                <span>Started {formatDate(subscription.startDate)}</span>
              </div>
            )}
            {subscription?.endDate && (
              <div className="bp-plan-hero__date-item">
                <Clock size={14} />
                <span>Renews {formatDate(subscription.endDate)}</span>
              </div>
            )}
            {isTrialActive && subscription?.trialExpiresAt && (
              <div className="bp-plan-hero__date-item">
                <AlertTriangle size={14} />
                <span>Trial ends {formatDate(subscription.trialExpiresAt)}</span>
              </div>
            )}
            {/* Renew button — monthly: 3 days before, quarterly: 7 days, yearly: 14 days */}
            {currentPlan && currentPlanHasPrice && !isTrialActive && (() => {
              const cycle = subscription?.billingCycle;
              const threshold = cycle === 'yearly' ? 14 : cycle === 'quarterly' ? 7 : 3;
              return (subscription?.daysRemaining != null && subscription.daysRemaining <= threshold) ||
                isExpired || subscription?.inGracePeriod;
            })() && (
              <button
                className="bp-plan-hero__renew-btn"
                onClick={() => handleUpgradeSelect(currentPlan)}
                disabled={paymentLoading}
              >
                <Zap size={14} /> Renew {currentPlan.name}
              </button>
            )}
          </div>
        </div>

        {/* ── Billing Cycle Toggle ── */}
        <div className="bp-cycle-toggle">
          {["monthly", "quarterly", "yearly"].map((c) => (
            <button
              key={c}
              className={`bp-cycle-toggle__btn ${billingCycle === c ? "bp-cycle-toggle__btn--active" : ""}`}
              onClick={() => setBillingCycle(c)}
            >
              {c.charAt(0).toUpperCase() + c.slice(1)}
              {c === "yearly" && <span className="bp-cycle-toggle__save">Save 20%</span>}
            </button>
          ))}
        </div>
      </div>

      {/* ── Resource Usage ── */}
      <div className="bp-section">
        <div className="bp-section__header">
          <TrendingUp size={18} />
          <h2 className="bp-section__title">Resource Usage</h2>
        </div>
        <div className="bp-usage-grid">
          {Object.entries(USAGE_KEYS).map(([usageKey, limitKey]) => {
            const u = usage?.[usageKey];
            const meta = LIMIT_LABELS[limitKey];
            if (!u || !meta) return null;
            return (
              <UsageMeter
                key={usageKey}
                label={meta.label}
                icon={meta.icon}
                used={u.used}
                max={u.max}
              />
            );
          })}
        </div>
      </div>

      {/* ── Current Features ── */}
      {currentPlan && (
        <div className="bp-section">
          <div className="bp-section__header">
            <Zap size={18} />
            <h2 className="bp-section__title">Your Features</h2>
          </div>
          <div className="bp-features-grid">
            {Object.entries(TOGGLE_LABELS).map(([key, label]) => (
              <FeatureItem key={key} label={label} enabled={!!currentPlan[key]} />
            ))}
          </div>
        </div>
      )}

      {/* ── All Plans Grid ── */}
      {upgradePlans.length > 0 && (
        <div className="bp-section" id="upgrade">
          <div className="bp-section__header">
            <Sparkles size={18} />
            <h2 className="bp-section__title">Choose Your Plan</h2>
            <p className="bp-section__subtitle">
              Compare all plans and pick the best fit for your institute
            </p>
          </div>
          <div className="bp-upgrade-grid">
            {upgradePlans.map((plan) => (
              <PlanCard
                key={plan._id}
                plan={plan}
                currentPlan={currentPlan}
                billingCycle={billingCycle}
                taxConfig={taxConfig}
                onSelect={handleUpgradeSelect}
                paymentLoading={paymentLoading}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Payment History ── */}
      <div className="bp-section">
        <div className="bp-section__header bp-section__header--clickable" onClick={() => setShowHistory((p) => !p)}>
          <History size={18} />
          <h2 className="bp-section__title">Payment History</h2>
          <ChevronRight size={16} className={`bp-section__chevron ${showHistory ? "bp-section__chevron--open" : ""}`} />
        </div>
        {showHistory && (
          payments.length > 0 ? (
            <div className="bp-history-table-wrap">
              <table className="bp-history-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Plan</th>
                    <th>Cycle</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Receipt</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p._id}>
                      <td>{formatDate(p.createdAt)}</td>
                      <td>{p.planId?.name || p.planName || "—"}</td>
                      <td className="bp-capitalize">{p.billingCycle}</td>
                      <td>{formatPrice(p.amount ? p.amount / 100 : 0)}</td>
                      <td>
                        <span className={`bp-pay-status bp-pay-status--${p.status}`}>
                          {p.status}
                        </span>
                      </td>
                      <td>
                        {p.status === "paid" && (
                          <button className="bp-receipt-btn" onClick={() => handleDownloadReceipt(p._id)}>
                            <Download size={14} /> Receipt
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="bp-empty-history">No payments yet.</p>
          )
        )}
      </div>

      {/* ── Need Help ── */}
      <div className="bp-help-card">
        <div className="bp-help-card__text">
          <h3>Need a custom plan?</h3>
          <p>Contact our team for enterprise pricing, custom limits, and priority support.</p>
          <p>Mail: <a href="mailto:support@lmscampus.com">support@lmscampus.com</a></p>
        </div>
        <button className="bp-help-card__btn" onClick={() => window.open("mailto:support@lmscampus.com")}>
          Contact Sales <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
};

export default MyPlanPage;
